'use client';

import { Box, Button, chakra, Flex, Input, Link, Text } from '@chakra-ui/react';
import { ChevronRight, Search } from 'lucide-react';
import NextLink from 'next/link';
import type { ReactNode } from 'react';

export function CapabilitySearch({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Box position="relative" flex="1" minW="180px" maxW={{ md: '360px' }}>
      <Box
        position="absolute"
        left="3"
        top="50%"
        transform="translateY(-50%)"
        color="ui.text.tertiary"
        pointerEvents="none"
      >
        <Search size={15} aria-hidden="true" />
      </Box>
      <Input
        aria-label={label}
        placeholder={label}
        value={value}
        onChange={event => onChange(event.target.value)}
        ps="9"
        h="9"
        fontSize="sm"
        bg="ui.surface.card"
        borderColor="ui.border.default"
        borderRadius="8px"
        _focusVisible={{
          borderColor: 'accent.fg',
          outline: '2px solid',
          outlineColor: 'accent.focusRing',
          outlineOffset: '1px',
        }}
      />
    </Box>
  );
}

export function CapabilityFilter({
  active,
  children,
  onClick,
  disabled,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      size="xs"
      variant="ghost"
      px="1"
      h="9"
      borderRadius="0"
      borderBottomWidth="2px"
      borderBottomColor={active ? 'ui.text.primary' : 'transparent'}
      flexShrink={0}
      bg="transparent"
      color={active ? 'ui.text.primary' : 'ui.text.secondary'}
      fontWeight={active ? '600' : '500'}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      _hover={{ bg: 'transparent', color: 'ui.text.primary' }}
    >
      {children}
    </Button>
  );
}

export function CapabilityTabLink({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <Link
      asChild
      px="1"
      py="2.5"
      borderBottomWidth="2px"
      borderBottomColor={active ? 'ui.text.primary' : 'transparent'}
      color={active ? 'ui.text.primary' : 'ui.text.secondary'}
      fontSize="sm"
      fontWeight={active ? '600' : '500'}
      flexShrink={0}
      _hover={{ color: 'ui.text.primary', textDecoration: 'none' }}
    >
      <NextLink href={href} aria-current={active ? 'page' : undefined}>
        {children}
      </NextLink>
    </Link>
  );
}

export function CapabilityStatus({
  label,
  color = 'ui.text.tertiary',
}: {
  label: string;
  color?: string;
}) {
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      gap="1.5"
      fontSize="xs"
      color="ui.text.secondary"
    >
      <Box as="span" boxSize="1.5" borderRadius="full" bg={color} flexShrink={0} />
      {label}
    </Box>
  );
}

export function CapabilityCatalogItem({
  icon,
  title,
  description,
  meta,
  onOpen,
  label,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  meta?: ReactNode;
  onOpen: () => void;
  label?: string;
}) {
  return (
    <chakra.button
      type="button"
      aria-label={label || `View ${title}`}
      aria-haspopup="dialog"
      onClick={onOpen}
      display="flex"
      alignItems="flex-start"
      gap="3"
      w="full"
      h="full"
      p="3.5"
      textAlign="left"
      bg="ui.surface.card"
      borderWidth="1px"
      borderColor="ui.border.default"
      borderRadius="8px"
      cursor="pointer"
      transition="background-color 140ms ease, border-color 140ms ease"
      _hover={{ bg: 'ui.surface.rowHover', borderColor: 'ui.border.accent' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'accent.fg', outlineOffset: '2px' }}
    >
      <Box
        as="span"
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxSize="8"
        flexShrink={0}
        borderRadius="6px"
        bg="ui.surface.input"
        color="ui.text.secondary"
        css={{ '& svg': { width: '16px', height: '16px' } }}
        aria-hidden="true"
      >
        {icon}
      </Box>
      <Box as="span" flex="1" minW={0}>
        <Text
          as="span"
          display="block"
          fontSize="sm"
          fontWeight="600"
          color="ui.text.primary"
          lineClamp={1}
        >
          {title}
        </Text>
        <Text
          as="span"
          display="block"
          mt="0.5"
          fontSize="xs"
          lineHeight="1.6"
          color="ui.text.secondary"
          lineClamp={2}
        >
          {description}
        </Text>
        {meta ? (
          <Flex
            as="span"
            mt="2"
            gap="2.5"
            align="center"
            flexWrap="wrap"
            fontSize="xs"
            color="ui.text.tertiary"
          >
            {meta}
          </Flex>
        ) : null}
      </Box>
      <Box as="span" color="ui.text.tertiary" mt="1" flexShrink={0} aria-hidden="true">
        <ChevronRight size={14} />
      </Box>
    </chakra.button>
  );
}
